import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { getCommissionsByRealtor, getCommissionSummary } from '../../../services/commissionService';
import Spinner from '../../../components/common/Spinner';

const PERIOD_CHIPS = ['이번 달', '지난 달', '3개월', '6개월', '1년'];
const TYPE_CHIPS = ['전체', '전환 리워드', '추천 리워드'];
const STATUS_CHIPS = ['전체', '정산완료'];

const TYPE_MAP = { 0: null, 1: 'conversion', 2: 'referral' };

const getMonthsAgo = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};

const CommissionDetail = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [periodIdx, setPeriodIdx] = useState(0);
  const [typeIdx, setTypeIdx] = useState(0);
  const [statusIdx, setStatusIdx] = useState(0);
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState({ withdrawable: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) return;
    setLoading(true);
    Promise.allSettled([
      getCommissionsByRealtor(userData.uid).then(setCommissions),
      getCommissionSummary(userData.uid).then(setSummary),
    ]).finally(() => setLoading(false));
  }, [userData?.uid]);

  const filtered = useMemo(() => {
    let result = [...commissions];

    // 기간 필터
    const cutoffs = [1, 1, 3, 6, 12];
    const cutoff = getMonthsAgo(cutoffs[periodIdx]);
    result = result.filter((c) => (c.createdAt || '') >= cutoff);

    // 유형 필터
    const typeKey = TYPE_MAP[typeIdx];
    if (typeKey) result = result.filter((c) => c.type === typeKey);

    // 상태 필터
    if (statusIdx === 1) result = result.filter((c) => c.is_settled);

    return result;
  }, [commissions, periodIdx, typeIdx, statusIdx]);

  if (loading) return <Spinner fullPage={false} />;

  return (
    <Container>
      <TopBlueCard>
        <TopBlueLabel>리워드 수익 (출금가능 / 전체 수익금)</TopBlueLabel>
        <TopBlueAmount>{summary.withdrawable.toLocaleString()} / {summary.total.toLocaleString()}</TopBlueAmount>
        <TopBlueBtn onClick={() => navigate('/realtor/mypage/withdraw')}>출금신청</TopBlueBtn>
      </TopBlueCard>

      <InfoRow>
        <IoInformationCircleOutline size={15} color="#9CA3AF" />
        <InfoText>본인인증 연동 시 자동 반영</InfoText>
      </InfoRow>

      <ChipRow>
        {PERIOD_CHIPS.map((c, i) => (
          <Chip key={c} $active={periodIdx === i} onClick={() => setPeriodIdx(i)}>{c}</Chip>
        ))}
      </ChipRow>
      <ChipRow>
        {TYPE_CHIPS.map((c, i) => (
          <Chip key={c} $active={typeIdx === i} onClick={() => setTypeIdx(i)}>{c}</Chip>
        ))}
      </ChipRow>
      <ChipRow>
        {STATUS_CHIPS.map((c, i) => (
          <Chip key={c} $active={statusIdx === i} onClick={() => setStatusIdx(i)}>{c}</Chip>
        ))}
      </ChipRow>

      {filtered.length === 0 ? (
        <EmptyBox>
          <EmptyIcon>!</EmptyIcon>
          <EmptyTitle>리워드 내역이 없어요</EmptyTitle>
          <EmptyDesc>완료·정산된 건이 있으면 여기에 표시됩니다.</EmptyDesc>
        </EmptyBox>
      ) : (
        <CommList>
          {filtered.map((c) => (
            <CommItem key={c.id}>
              <CommLeft>
                <CommType>{c.type === 'conversion' ? '전환 리워드' : '추천 리워드'}</CommType>
                <CommDate>{c.createdAt}</CommDate>
              </CommLeft>
              <CommAmount>{c.amount.toLocaleString()}원</CommAmount>
            </CommItem>
          ))}
        </CommList>
      )}

      <CenterBtnWrap>
        <PillBtn onClick={() => navigate('/realtor/mypage/performance')}>실적 보기</PillBtn>
      </CenterBtnWrap>
    </Container>
  );
};

export default CommissionDetail;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TopBlueCard = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const TopBlueLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const TopBlueAmount = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: #fff;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const TopBlueBtn = styled.button`
  width: 100%;
  height: 44px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const InfoText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
`;

const ChipRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const Chip = styled.button`
  padding: 6px 14px;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.bgWhite};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const EmptyDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
`;

const CenterBtnWrap = styled.div`
  display: flex;
  justify-content: center;
`;

const PillBtn = styled.button`
  padding: 12px 40px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
`;

const CommList = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const CommItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.base} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const CommLeft = styled.div``;

const CommType = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const CommDate = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-top: 2px;
`;

const CommAmount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;
